<script setup>
import { ref, computed, watch } from 'vue'
import TokenPopup from './TokenPopup.vue'
import axios from '../axios.js'

const props = defineProps({
  user: Object,
  userStore: Object,
  selfService: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['token-generated', 'token-revoked', 'token-rotated'])

// State
const userTokens = ref([])
const loadingTokens = ref(false)
const showCreateTokenModal = ref(false)
const showTokenPopup = ref(false)
const generatedToken = ref(null)
const newTokenName = ref('')
const newTokenAbilities = ref(['*'])
const newTokenExpiration = ref('10') // years
const creatingToken = ref(false)
const selectedToken = ref(null)
const showTokenDetailsModal = ref(false)
const showRotateModal = ref(false)
const tokenToRotate = ref(null)
const rotatingToken = ref(false)

// Available token abilities
const availableTokenAbilities = [
  { key: '*', name: 'All Permissions', description: 'Full access to all API endpoints' },
  { key: 'fixtures.read', name: 'Read Fixtures', description: 'View fixture data and information' },
  { key: 'fixtures.write', name: 'Write Fixtures', description: 'Create and modify fixtures' },
  { key: 'markets.read', name: 'Read Markets', description: 'View market data and odds' },
  { key: 'markets.write', name: 'Write Markets', description: 'Create and modify markets' },
  { key: 'players.read', name: 'Read Players', description: 'View player data and statistics' },
  { key: 'players.write', name: 'Write Players', description: 'Create and modify player data' },
  { key: 'teams.read', name: 'Read Teams', description: 'View team information' },
  { key: 'teams.write', name: 'Write Teams', description: 'Create and modify team data' },
  { key: 'reports.read', name: 'Read Reports', description: 'Access to reporting data' },
  { key: 'analytics.read', name: 'Read Analytics', description: 'Access to analytics data' },
  { key: 'users.read', name: 'Read Users', description: 'View user information (limited)' },
  { key: 'api.read', name: 'API Read Only', description: 'Read-only access to all API endpoints' }
]

// Computed
const visibleTokens = computed(() => {
  if (!props.selfService) return userTokens.value
  return userTokens.value.filter(token => !token.name?.startsWith('web-login-'))
})

const activeTokensCount = computed(() => {
  return visibleTokens.value.filter(t => getTokenStatus(t).status === 'Active').length
})

const expiredTokensCount = computed(() => {
  return visibleTokens.value.filter(t => getTokenStatus(t).status === 'Expired').length
})

const disabledTokensCount = computed(() => {
  return visibleTokens.value.filter(t => getTokenStatus(t).status === 'Disabled').length
})

const canRotateToken = (token) => {
  if (!token || token.name?.startsWith('web-login-')) return false
  if (token.expires_at && new Date(token.expires_at) < new Date()) return false
  return true
}

// Methods
const fetchUserTokens = async () => {
  if (!props.user) return
  
  loadingTokens.value = true
  try {
    const response = await axios.get(`/api/users/${props.user.id}/tokens`)
    userTokens.value = response.data || []
  } catch (error) {
    console.error('Failed to fetch user tokens:', error)
    userTokens.value = []
  } finally {
    loadingTokens.value = false
  }
}

const createApiToken = async () => {
  if (!newTokenName.value.trim()) {
    alert('Token name is required')
    return
  }
  
  creatingToken.value = true
  try {
    // Calculate expiration date
    const expirationDate = new Date()
    expirationDate.setFullYear(expirationDate.getFullYear() + parseInt(newTokenExpiration.value))
    
    const response = await axios.post(`/api/users/${props.user.id}/tokens`, {
      name: newTokenName.value,
      abilities: props.selfService ? ['*'] : newTokenAbilities.value,
      expires_at: expirationDate.toISOString()
    })
    
    if (response.data) {
      // Add to local tokens array
      userTokens.value.push(response.data)
      
      // Show token popup
      generatedToken.value = {
        token: response.data.token,
        name: newTokenName.value,
        type: 'api',
        expiresAt: response.data.expires_at,
        abilities: response.data.abilities
      }
      showTokenPopup.value = true
      
      // Reset form
      newTokenName.value = ''
      newTokenAbilities.value = ['*']
      newTokenExpiration.value = '10'
      showCreateTokenModal.value = false
      
      // Emit event
      emit('token-generated', response.data)
    }
  } catch (error) {
    console.error('Failed to create token:', error)
    alert('Failed to create token: ' + (error.response?.data?.message || error.message))
  } finally {
    creatingToken.value = false
  }
}

const openRotateModal = (token) => {
  tokenToRotate.value = token
  showRotateModal.value = true
}

const closeRotateModal = () => {
  if (!rotatingToken.value) {
    showRotateModal.value = false
    tokenToRotate.value = null
  }
}

const confirmRotateToken = async () => {
  if (!tokenToRotate.value || !props.user) return

  rotatingToken.value = true
  try {
    const response = await axios.post(`/api/users/${props.user.id}/tokens/${tokenToRotate.value.id}/rotate`)
    if (response.data) {
      userTokens.value = userTokens.value.filter(token => token.id !== tokenToRotate.value.id)
      userTokens.value.push(response.data)

      generatedToken.value = {
        token: response.data.token,
        name: getTokenDisplayName(response.data.name || tokenToRotate.value.name),
        type: 'api',
        expiresAt: response.data.expires_at,
        abilities: response.data.abilities
      }
      showTokenPopup.value = true
      emit('token-rotated', response.data)

      showRotateModal.value = false
      tokenToRotate.value = null
    }
  } catch (error) {
    console.error('Failed to rotate token:', error)
    alert('Failed to rotate token: ' + (error.response?.data?.message || error.message))
  } finally {
    rotatingToken.value = false
  }
}

const revokeToken = async (tokenId) => {
  if (!confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
    return
  }
  
  try {
    await axios.delete(`/api/users/${props.user.id}/tokens/${tokenId}`)
    
    // Remove from local array
    userTokens.value = userTokens.value.filter(token => token.id !== tokenId)
    
    console.log('Token revoked successfully')
    emit('token-revoked', tokenId)
  } catch (error) {
    console.error('Failed to revoke token:', error)
    alert('Failed to revoke token: ' + (error.response?.data?.message || error.message))
  }
}

const toggleTokenStatus = async (tokenId) => {
  const token = userTokens.value.find(t => t.id === tokenId)
  if (!token) return
  
  const newStatus = !token.disabled
  const action = newStatus ? 'disable' : 'enable'
  
  if (!confirm(`Are you sure you want to ${action} this token?`)) {
    return
  }
  
  try {
    await axios.patch(`/api/users/${props.user.id}/tokens/${tokenId}`, {
      disabled: newStatus
    })
    
    // Update local token status
    token.disabled = newStatus
    
    console.log(`Token ${action}d successfully`)
  } catch (error) {
    console.error(`Failed to ${action} token:`, error)
    alert(`Failed to ${action} token: ` + (error.response?.data?.message || error.message))
  }
}

const viewTokenDetails = (token) => {
  selectedToken.value = token
  showTokenDetailsModal.value = true
}

const closeTokenDetailsModal = () => {
  showTokenDetailsModal.value = false
  selectedToken.value = null
}

const getTokenStatus = (token) => {
  // Check if token is disabled first
  if (token.disabled) {
    return { status: 'Disabled', class: 'bg-gray-100 text-gray-800' }
  }
  
  if (!token.expires_at) return { status: 'Active', class: 'bg-green-100 text-green-800' }
  
  const now = new Date()
  const expiryDate = new Date(token.expires_at)
  
  if (expiryDate < now) {
    return { status: 'Expired', class: 'bg-red-100 text-red-800' }
  }
  
  const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
  
  if (daysUntilExpiry <= 1) {
    return { status: 'Expires Soon', class: 'bg-yellow-100 text-yellow-800' }
  } else if (daysUntilExpiry <= 7) {
    return { status: 'Expires This Week', class: 'bg-orange-100 text-orange-800' }
  } else {
    return { status: 'Active', class: 'bg-green-100 text-green-800' }
  }
}

const formatLastUsed = (lastUsedAt) => {
  if (!lastUsedAt) return 'Never'
  return new Date(lastUsedAt).toLocaleString()
}

const formatExpiration = (expiresAt) => {
  if (!expiresAt) return 'Never expires'
  return new Date(expiresAt).toLocaleDateString()
}

const getTokenType = (tokenName) => {
  if (tokenName?.startsWith('web-login-')) return 'web'
  if (tokenName?.startsWith('api-integration-')) return 'api'
  return 'api'
}

const getTokenDisplayName = (tokenName) => {
  if (tokenName?.startsWith('api-integration-')) {
    return tokenName.replace('api-integration-', '')
  }
  if (tokenName?.startsWith('web-login-')) {
    return 'Web Login Token'
  }
  return tokenName
}

watch(() => props.user?.id, () => {
  fetchUserTokens()
}, { immediate: true })
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div
          v-if="selfService"
          class="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center"
        >
          <i class="fas fa-key text-indigo-600"></i>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">{{ selfService ? 'API Keys' : 'API Token Management' }}</h3>
          <p class="text-sm text-gray-600">
            {{ selfService
              ? 'Generate and rotate API keys for your integrations. The full key is shown only once.'
              : `Manage long-lived API integration tokens for ${user?.name}` }}
          </p>
        </div>
      </div>
      
      <div class="flex items-center gap-4">
        <!-- Stats -->
        <div class="flex gap-4 text-center">
          <div>
            <div class="text-xl font-bold text-gray-900">{{ visibleTokens.length }}</div>
            <div class="text-xs text-gray-600">{{ selfService ? 'Total Keys' : 'Total Tokens' }}</div>
          </div>
          <div>
            <div class="text-xl font-bold text-green-600">{{ activeTokensCount }}</div>
            <div class="text-xs text-gray-600">Active</div>
          </div>
          <div v-if="disabledTokensCount > 0">
            <div class="text-xl font-bold text-gray-600">{{ disabledTokensCount }}</div>
            <div class="text-xs text-gray-600">Disabled</div>
          </div>
          <div v-if="expiredTokensCount > 0">
            <div class="text-xl font-bold text-red-600">{{ expiredTokensCount }}</div>
            <div class="text-xs text-gray-600">Expired</div>
          </div>
        </div>
        
        <!-- Create Token Button -->
        <button 
          @click="showCreateTokenModal = true"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-plus"></i>
          {{ selfService ? 'Generate API Key' : 'Create API Token' }}
        </button>
      </div>
    </div>

    <!-- Tokens Table -->
    <div class="space-y-4">
      <!-- Loading State -->
      <div v-if="loadingTokens" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>

      <!-- Tokens Table -->
      <div v-else-if="visibleTokens.length > 0" class="bg-white border rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token Name</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abilities</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="token in visibleTokens" :key="token.id" class="hover:bg-gray-50">
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-gray-900">{{ getTokenDisplayName(token.name) }}</div>
                <div class="text-xs text-gray-500">ID: {{ token.id }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <i :class="getTokenType(token.name) === 'web' ? 'fas fa-globe text-blue-500' : 'fas fa-code text-green-500'"></i>
                  <span class="text-sm">{{ getTokenType(token.name) === 'web' ? 'Web' : 'API' }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1">
                  <span v-for="ability in (token.abilities || [])" :key="ability"
                        class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    {{ ability === '*' ? 'All' : ability }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-gray-500">
                {{ formatLastUsed(token.last_used_at) }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-500">
                {{ formatExpiration(token.expires_at) }}
              </td>
              <td class="px-4 py-3">
                <span :class="[
                  'px-2 py-1 text-xs rounded-full font-medium',
                  getTokenStatus(token).class
                ]">
                  {{ getTokenStatus(token).status }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-4">
                  <button 
                    @click="viewTokenDetails(token)"
                    class="text-blue-600 hover:text-blue-700 text-sm"
                    title="View token details"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    v-if="canRotateToken(token)"
                    @click="openRotateModal(token)"
                    class="text-indigo-600 hover:text-indigo-700 text-sm"
                    title="Rotate key"
                  >
                    <i class="fas fa-sync-alt"></i>
                  </button>
                  <button 
                    v-if="!selfService"
                    @click="toggleTokenStatus(token.id)"
                    class="text-sm transition-colors"
                    :class="token.disabled ? 'text-green-600 hover:text-green-700' : 'text-yellow-600 hover:text-yellow-700'"
                    :title="token.disabled ? 'Enable token' : 'Disable token'"
                  >
                    <i :class="token.disabled ? 'fas fa-play' : 'fas fa-pause'"></i>
                  </button>
                  <button 
                    @click="revokeToken(token.id)"
                    class="text-red-600 hover:text-red-700 text-sm"
                    :title="selfService ? 'Revoke key' : 'Revoke token'"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Tokens State -->
      <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
        <div class="text-gray-400 mb-4">
          <i class="fas fa-key text-4xl"></i>
        </div>
        <div class="text-gray-600 mb-2">{{ selfService ? 'No API keys yet' : 'No API tokens found' }}</div>
        <div class="text-sm text-gray-500 mb-4">
          {{ selfService
            ? 'Generate an API key to authenticate your integrations'
            : "This user doesn't have any API tokens yet" }}
        </div>
        <button 
          @click="showCreateTokenModal = true"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i class="fas fa-plus mr-2"></i>
          {{ selfService ? 'Generate First API Key' : 'Create First API Token' }}
        </button>
      </div>
    </div>

    <!-- Create Token Modal -->
    <div v-if="showCreateTokenModal" 
         class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-center justify-center overflow-y-auto"
         @click.self="showCreateTokenModal = false"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-8">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">{{ selfService ? 'Generate API Key' : 'Create API Token' }}</h3>
            <p class="text-sm text-gray-600 mt-1">
              {{ selfService ? 'Create a new key for your integrations. Copy it immediately — it is shown only once.' : 'Generate a long-lived token for API integrations' }}
            </p>
          </div>
          <button @click="showCreateTokenModal = false" 
                  class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Form -->
        <div class="p-6 space-y-4">
          <!-- Token Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Token Name <span class="text-red-500">*</span>
            </label>
            <input 
              v-model="newTokenName"
              type="text" 
              placeholder="e.g., C++ Queue Server, Mobile App, Webhook"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :class="{ 'border-red-300': !newTokenName }"
            />
            <p class="text-xs text-gray-500 mt-1">
              Give your token a descriptive name to identify its purpose
            </p>
          </div>

          <!-- Token Expiration -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Expiration Period
            </label>
            <select 
              v-model="newTokenExpiration"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="1">1 Year</option>
              <option value="2">2 Years</option>
              <option value="5">5 Years</option>
              <option value="10">10 Years</option>
              <option value="0">Never Expires</option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              API tokens are designed to be long-lived for stable integrations
            </p>
          </div>

          <!-- Token Abilities -->
          <div v-if="!selfService">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Token Abilities
            </label>
            <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              <label v-for="ability in availableTokenAbilities" :key="ability.key" class="flex items-start gap-3">
                <input 
                  v-model="newTokenAbilities"
                  type="checkbox" 
                  :value="ability.key"
                  class="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900">{{ ability.name }}</div>
                  <div class="text-xs text-gray-500">{{ ability.description }}</div>
                </div>
              </label>
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Select the permissions this token should have
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button 
            @click="showCreateTokenModal = false"
            class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            :disabled="creatingToken"
          >
            Cancel
          </button>
          <button 
            @click="createApiToken"
            :disabled="creatingToken || !newTokenName"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <div v-if="creatingToken" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
            <i v-else class="fas fa-key"></i>
            {{ creatingToken ? (selfService ? 'Generating...' : 'Creating...') : (selfService ? 'Generate Key' : 'Create Token') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Token Details Modal -->
    <div v-if="showTokenDetailsModal" 
         class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto"
         @click.self="closeTokenDetailsModal"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-8">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Token Details</h3>
            <p class="text-sm text-gray-600 mt-1">View token information</p>
          </div>
          <button @click="closeTokenDetailsModal" 
                  class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="p-6">
          <div v-if="selectedToken" class="space-y-4">
            <!-- Token Information -->
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="font-medium text-gray-700">Name:</span>
                <div class="text-gray-900">{{ getTokenDisplayName(selectedToken.name) }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">ID:</span>
                <div class="text-gray-900">{{ selectedToken.id }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Type:</span>
                <div class="flex items-center gap-2">
                  <i :class="getTokenType(selectedToken.name) === 'web' ? 'fas fa-globe text-blue-500' : 'fas fa-code text-green-500'"></i>
                  <span>{{ getTokenType(selectedToken.name) === 'web' ? 'Web Login' : 'API Integration' }}</span>
                </div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Created:</span>
                <div class="text-gray-900">{{ new Date(selectedToken.created_at).toLocaleString() }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Last Used:</span>
                <div class="text-gray-900">{{ formatLastUsed(selectedToken.last_used_at) }}</div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Expires:</span>
                <div class="text-gray-900">
                  {{ formatExpiration(selectedToken.expires_at) }}
                </div>
              </div>
              <div>
                <span class="font-medium text-gray-700">Status:</span>
                <div>
                  <span :class="[
                    'px-2 py-1 text-xs rounded-full font-medium',
                    getTokenStatus(selectedToken).class
                  ]">
                    {{ getTokenStatus(selectedToken).status }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Abilities -->
            <div>
              <span class="font-medium text-gray-700 text-sm">Abilities:</span>
              <div class="flex flex-wrap gap-2 mt-2">
                <span v-for="ability in (selectedToken.abilities || [])" :key="ability"
                      class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {{ ability === '*' ? 'All Permissions' : ability }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-between items-center p-6 border-t bg-gray-50 rounded-b-xl">
          <div class="flex items-center gap-2">
            <button 
              v-if="selectedToken && canRotateToken(selectedToken)"
              @click="openRotateModal(selectedToken); closeTokenDetailsModal()"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <i class="fas fa-sync-alt"></i>
              Rotate Key
            </button>
            <button 
              v-if="selectedToken && !selfService"
              @click="toggleTokenStatus(selectedToken.id)"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              :class="selectedToken.disabled 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-yellow-600 hover:bg-yellow-700 text-white'"
            >
              <i :class="selectedToken.disabled ? 'fas fa-play' : 'fas fa-pause'"></i>
              {{ selectedToken.disabled ? 'Enable Token' : 'Disable Token' }}
            </button>
          </div>
          
          <button 
            @click="closeTokenDetailsModal"
            class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Rotate Key Confirmation Modal -->
    <div v-if="showRotateModal"
         class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[135] flex items-center justify-center overflow-y-auto p-4"
         @click.self="closeRotateModal"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div class="p-6">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <i class="fas fa-sync-alt text-indigo-600 text-xl"></i>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-semibold text-gray-900">Rotate this API key?</h3>
              <p class="mt-2 text-sm text-gray-600">
                Rotating
                <span class="font-medium text-gray-900">{{ getTokenDisplayName(tokenToRotate?.name) }}</span>
                will immediately invalidate the current secret. Copy the new key — it is shown only once.
              </p>
            </div>
          </div>
        </div>
        <div class="px-6 py-4 bg-gray-50 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            @click="closeRotateModal"
            :disabled="rotatingToken"
            class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="confirmRotateToken"
            :disabled="rotatingToken"
            class="px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
          >
            <i v-if="rotatingToken" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-sync-alt"></i>
            {{ rotatingToken ? 'Rotating...' : 'Yes, rotate key' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Token Popup -->
    <TokenPopup
      :show="showTokenPopup"
      :token="generatedToken?.token"
      :token-name="generatedToken?.name"
      :token-type="generatedToken?.type"
      :expires-at="generatedToken?.expiresAt"
      :abilities="generatedToken?.abilities"
      @close="showTokenPopup = false"
      @copy="(token) => console.log('Token copied:', token)"
    />
  </div>
</template> 